import {
    DateFormatHelper,
    DetailItem,
    DetailSection,
    PublicationEnums,
    AppEnumHelper,
    dateCompare,
    DOMAIN_TYPES,
    formatName,
    PropertyEnums
} from "@/Shared";
import { AuditViewModel } from '@/Shared/Logic/ViewModels/AuditViewModel';
import { AuditModel } from '@/Shared/Logic/Models/AuditModel';
import { AccessRightModel } from '@/Domains/Auth';
import { PublicationModel } from "../Models/PublicationModel";
import { PropertyViewModel } from "@/Domains/Property";

export interface PublicationVMParams {
    publication: PublicationModel;
    viewEnums: PublicationEnums;
    propertyEnums : PropertyEnums;
    accessRight: AccessRightModel;
    isDisplaying: boolean;
}

export class PublicationViewModel {

    public id: number;
    public model!: PublicationModel;
    public adSourceCategory: string;
    public adSourceType: string;
    public propertyId: number;
    public firstPostedAt: string;
    public endPostedAt: string;
    public firstPostedBy: string;
    public endPostedBy: string;
    public note: string;

    public createdAt!: string;

    public cardHeaderTitle!: string;
    public cardClass!: string;
    public isDeleted!: boolean;

    public audit!: AuditViewModel;
    private viewEnums!: PublicationEnums;


    public constructor(private params: PublicationVMParams) {

        const { viewEnums, publication, isDisplaying } = this.params;

        this.viewEnums = viewEnums;
        this.model = publication;

        this.id = publication?.id || 0;
        this.audit = new AuditViewModel(publication as AuditModel);

        // Mapping des enums
        this.adSourceCategory = AppEnumHelper.getLabel(this.viewEnums.AdSourceCategoryEnum, publication.adSourceCategory);
        this.adSourceType = AppEnumHelper.getLabel(this.viewEnums.AdSourceTypeEnum, publication.adSourceType);

        this.propertyId = publication.propertyId;
        this.firstPostedAt = DateFormatHelper.toLocaleDateString(publication.firstPostedAt);
        this.endPostedAt = DateFormatHelper.toLocaleDateString(publication.endPostedAt);

        // Utilisation des ByUser pour afficher le nom complet
        this.firstPostedBy = formatName(publication.firstPostedByUser?.person);
        this.endPostedBy = formatName(publication.endPostedByUser?.person);

        this.note = publication.note || "";

        this.cardHeaderTitle = `${this.adSourceCategory} - ${this.adSourceType}`;
        this.isDeleted = !!publication.deletedAt;
        this.cardClass = isDisplaying ? 'active' : '';

        this.createdAt = DateFormatHelper.toLocaleString(publication.createdAt) || "";
    }

    get title() {
        return `Annonce N° ${this.id} - ${this.adSourceCategory}`;
    }

    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getGlobalSection,
                legend: 'Caractéristiques Générales',
                type: 'box'
            },
            {
                sectionFn: this.getPropertySection,
                legend: 'Bien associé',
                type: 'box'
            }
        ];

        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Publication)) {
            sections.push({
                sectionFn: this.audit.getAuditTrail,
                legend: 'Traçabilité',
                type: 'box'
            });
        }

        return sections;
    }

    getPropertySection = () => {
        if(this.model.property) {
            const propertyVM = new PropertyViewModel({
                property: this.model.property,
                viewEnums: this.params.propertyEnums,
                accessRight : this.params.accessRight,
                isDisplaying : false
            });
            return propertyVM.getGlobalSection()
        }
        return [] as DetailItem[];
    }

    getGlobalSection = () => {
        return [
            { label: "N° Annonce", value: this.id },
            { label: "Catégorie", value: this.adSourceCategory },
            { label: "Type", value: this.adSourceType },
            { label: "Bien associé", value: this.propertyId ?? "-" },
            { label: "Publié le", value: this.firstPostedAt ?? "-" },
            { label: "Fin de publication", value: this.endPostedAt ?? "-" },
            { label: "Publiée par", value: this.firstPostedBy ?? "-" },
            { label: "Fin de publication par", value: this.endPostedBy ?? "-" },
            { label: "Note", value: this.note || "-" },
        ] as DetailItem[];
    }

    compareTo(other: PublicationViewModel): number {
        return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
    }
}
